import { useState, useRef, useEffect } from 'react';
import { Button, Box, Stack } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { RichTextField } from 'ra-ui-materialui';

interface ExpandableRichTextFieldProps {
    source: string;
    variant?: 'body1' | 'body2' | 'caption';
    maxHeight?: number;
    showButton?: boolean;
    buttonText?: {
        expand: string;
        collapse: string;
    };
}

export const ExpandableRichTextField = ({
    source,
    variant = 'body2',
    maxHeight = 100,
    showButton = true,
    buttonText = {
        expand: 'Show more',
        collapse: 'Show less',
    },
}: ExpandableRichTextFieldProps) => {
    const [expanded, setExpanded] = useState(false);
    const [needsTruncation, setNeedsTruncation] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (contentRef.current) {
            const element = contentRef.current;
            const actualHeight = element.scrollHeight;
            setNeedsTruncation(actualHeight > maxHeight);
        }
    }, [source, maxHeight]);

    return (
        <Stack direction="column" gap={1} justifyContent="flex-start">
            <Box
                ref={contentRef}
                sx={{
                    maxHeight: expanded ? 'none' : `${maxHeight}px`,
                    overflow: 'hidden',
                }}
            >
                <RichTextField source={source} variant={variant} />
            </Box>

            {showButton && needsTruncation && (
                <Button
                    variant="text"
                    size="small"
                    onClick={() => setExpanded(!expanded)}
                    startIcon={expanded ? <ExpandLess /> : <ExpandMore />}
                >
                    {expanded ? buttonText.collapse : buttonText.expand}
                </Button>
            )}
        </Stack>
    );
};
